import React, { useEffect, useState } from 'react';
import { compact } from 'lodash';
import { useHistory, useLocation } from 'react-router';
import { Page, Tabs, Typography, Divider, PageContentHeader, Callout, Button, useOverlayContext, ConfirmationModal } from 'seagull';
import { routes } from "../../constants";
import { selectors } from "../../store/state-selectors";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { useStore, useStoreActions } from "../../contexts";
import { handleRequest, numberAndUnit } from "../../infra/utils";
import { helpArticles } from "../../utils/helpArticles";
import { NotificationSettingsTable } from "./NotificationSettingsTable";
import { Tab, Table, isDays, minutesToDays, getRecipientTypesText, reminderColumnDefsMap, reminderHeaderItemsMap, notificationColumnDefs, notificationHeaderItems } from "./NotificationSettings.utils";
import styles from "./NotificationSettings.module.scss";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { EmailOnBehalfSettingsModel } from "../../models/EmailOnBehalfSettingsModel";
import { SendEmailOnBehalfModal } from "./SendEmailOnBehalfModal";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { MailSentLiner24 } from 'seagull/icons';
const NotificationSettings = () => {
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const businessId = useStore(selectors.user.businessId);
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(Tab.Vendor);
  const {
    EMAIL_ON_BEHALF = false
  } = useFeatureFlags();
  const overlayContext = useOverlayContext();
  const [emailOnBehalf, setEmailOnBehalf] = useState(EmailOnBehalfSettingsModel.makeDisconnected());
  const {
    startLoader,
    stopLoader,
    showErrorToast
  } = useOverlayContext();
  const {
    logBI
  } = useStoreActions();
  const fetchData = async () => {
    if (!businessId) {
      history.push(routes.home);
      return {};
    }
    return api.notification.getNotificationSettings(businessId).then(data => {
      setEmailOnBehalf(data.sendOnBehalfOf);
      return data;
    });
  };
  const {
    refresh,
    renderRestfulState
  } = useRestfulWrapper({
    fetch: fetchData
  });
  useEffect(() => {
    const handleGmailAuth = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const scope = params.get('scope');
      if (EMAIL_ON_BEHALF && code && scope && businessId) {
        startLoader();
        const {
          success
        } = await handleRequest(api.notification.completeGmailAuthorization(businessId, code, scope));
        if (success) {
          logBI(18502, {
            vendorId: businessId
          });
          await refresh();
          setSelectedTab(Tab.Client);
        } else {
          logBI(18503, {
            vendorId: businessId
          });
          showErrorToast('Failed to connect gmail account - please try again', 'complete-gmail-auth-error-toast');
        }
        history.replace(location.pathname);
        stopLoader();
      }
    };
    handleGmailAuth();
  }, [EMAIL_ON_BEHALF, businessId, location.pathname, location.search]);
  const connectEmailOnBehalf = async () => {
    logBI(18500, {
      vendorId: businessId
    });
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(SendEmailOnBehalfModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  const disconnectEmailOnBehalf = async () => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          skin: "danger",
          title: "Are you sure?",
          textAlignCenter: false,
          content: /*#__PURE__*/React.createElement(Typography, null, "Once disconnected, emails to clients will be sent from", ' ', /*#__PURE__*/React.createElement(Typography, {
            as: "span",
            weight: "bold"
          }, "system@notifications.sayanchor.com")),
          primaryButtonLabel: "Disconnect",
          secondaryButtonLabel: "Cancel",
          secondaryButtonTestid: "disconnect-modal-cancel-button",
          primaryButtonTestid: "disconnect-modal-primary-button",
          onCloseButtonClick: dismissModal,
          onSecondaryButtonClick: dismissModal,
          onPrimaryButtonClick: async () => {
            if (businessId) {
              startLoader();
              const {
                success
              } = await handleRequest(api.notification.disconnectEmailOnBehalf(businessId));
              if (success) {
                setEmailOnBehalf(EmailOnBehalfSettingsModel.makeDisconnected());
                dismissModal();
                logBI(18504, {
                  vendorId: businessId
                });
              } else {
                showErrorToast('Failed to disconnect gmail account - please try again', 'disconnect-email-on-behalf-error-toast');
              }
              stopLoader();
            }
          }
        });
      }
    });
  };
  return /*#__PURE__*/React.createElement(Page, {
    header: "Notification Settings",
    subheader: /*#__PURE__*/React.createElement(React.Fragment, null, "Set which notifications you and your clients will receive.", /*#__PURE__*/React.createElement(ExternalLinkButton, {
      href: helpArticles.notificationSettings(),
      "data-testid": 'learn-more-link',
      openInNewTab: true,
      className: styles.learnMoreLink
    }, "Learn more")),
    "data-testid": "notification-settings"
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(Tabs, {
    value: selectedTab,
    onChange: (_, value) => setSelectedTab(value),
    tabs: [{
      component: 'Notifications sent to your team',
      id: Tab.Vendor,
      value: Tab.Vendor,
      testId: "vendor-notifications-tab"
    }, {
      component: 'Notifications sent to your clients',
      id: Tab.Client,
      value: Tab.Client,
      testId: "client-notifications-tab"
    }]
  })), renderRestfulState({
    renderChild: _ref3 => {
      let {
        data,
        loading,
        refresh
      } = _ref3;
      return /*#__PURE__*/React.createElement(React.Fragment, null, !loading && EMAIL_ON_BEHALF && selectedTab === Tab.Client && !emailOnBehalf.isDisconnected() && /*#__PURE__*/React.createElement(Callout, {
        icon: /*#__PURE__*/React.createElement(MailSentLiner24, null),
        className: styles.mailCallout,
        "data-testid": 'connect-email-on-behalf-callout',
        variant: "paragraph1",
        withAction: emailOnBehalf.isDisconnected() ? /*#__PURE__*/React.createElement(Button, {
          skin: "primary",
          "data-testid": 'connect-email-on-behalf-button',
          onClick: connectEmailOnBehalf,
          size: "small"
        }, "Send emails using my own domain") : /*#__PURE__*/React.createElement(Button, {
          skin: "primary",
          "data-testid": 'disconnect-email-on-behalf-button',
          onClick: disconnectEmailOnBehalf,
          size: "small"
        }, "Disconnect")
      }, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": 'connect-email-on-behalf-text'
      }, "Client Notifications are sent from", ' ', /*#__PURE__*/React.createElement(Typography, {
        as: "span",
        weight: "bold"
      }, emailOnBehalf.email()))), /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Notifications, "-table-title"),
        variant: "h6",
        className: styles.tableTitle
      }, 'Notifications triggered when an event occurs'), /*#__PURE__*/React.createElement(NotificationSettingsTable, {
        data: data,
        refresh: refresh,
        tableType: Table.Notifications,
        columnDefs: notificationColumnDefs,
        headerItems: notificationHeaderItems,
        selectedTab: selectedTab,
        getRowItems: _ref4 => {
          let {
            notification,
            text,
            key
          } = _ref4;
          return [{
            value: /*#__PURE__*/React.createElement(Typography, {
              weight: "normal"
            }, text),
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-title")
          }, {
            value: /*#__PURE__*/React.createElement(Typography, null, notification.recipientTypes.length ? 'Active' : 'Inactive'),
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-status")
          }, {
            value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/React.createElement(Typography, {
              key: text,
              as: "div",
              className: styles.preserveLineBreak
            }, text)),
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-notified-team-members")
          }];
        }
      }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Reminders, "-table-title"),
        variant: "h6",
        className: styles.tableTitle
      }, 'Notifications triggered when an action is delayed'), selectedTab === Tab.Client && /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Reminders, "-subtitle"),
        skin: "secondary",
        variant: "paragraph1"
      }, 'Notifications are sent at fixed intervals, up to a selected number of retries'), /*#__PURE__*/React.createElement(NotificationSettingsTable, {
        data: data,
        refresh: refresh,
        tableType: Table.Reminders,
        columnDefs: reminderColumnDefsMap[selectedTab],
        headerItems: reminderHeaderItemsMap[selectedTab],
        selectedTab: selectedTab,
        getRowItems: _ref5 => {
          let {
            notification,
            text,
            key
          } = _ref5;
          return compact([{
            value: /*#__PURE__*/React.createElement(Typography, {
              weight: "normal"
            }, text),
            'data-testid': "reminders-row-".concat(key, "-title")
          }, {
            value: /*#__PURE__*/React.createElement(Typography, null, notification.recipientTypes.length ? 'Active' : 'Inactive'),
            'data-testid': "reminders-row-".concat(key, "-status")
          }, {
            value: notification.recipientTypes.length ? isDays(notification.recurring.minutes) ? numberAndUnit(minutesToDays(notification.recurring.minutes), 'day') : numberAndUnit(notification.recurring.minutes, 'minute') : '',
            'data-testid': "reminders-row-".concat(key, "-").concat(selectedTab === Tab.Client ? 'interval' : 'delay')
          }, selectedTab === Tab.Client && {
            value: notification.recipientTypes.length ? notification.recurring.times : '',
            'data-testid': "reminders-row-".concat(key, "-retries")
          }, {
            value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/React.createElement(Typography, {
              key: text,
              as: "div",
              className: styles.preserveLineBreak
            }, text)),
            'data-testid': "reminders-row-".concat(key, "-notified-team-members")
          }]);
        }
      }));
    }
  }));
};
export default NotificationSettings;